import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';

import FileDropZone from './FileDropZone';
import PreviousFilesList from './PreviousFilesList';
import { useAnalyzeFile } from '../../hooks/useLeadImportApi';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import type { FileInfo } from '../../utils/types';

/**
 * FileUploadStep orchestrates the file upload experience.
 * It renders the FileDropZone for new uploads and PreviousFilesList
 * for selecting previously uploaded files.
 *
 * After a successful upload or file selection, it triggers file analysis
 * and advances to the analysis step.
 */
const FileUploadStep: React.FC = () => {
  const queryClient = useQueryClient();
  const analyzeFile = useAnalyzeFile();
  const setFileData = useImportWizardStore((s) => s.setFileData);
  const setAnalysisData = useImportWizardStore((s) => s.setAnalysisData);
  const setStep = useImportWizardStore((s) => s.setStep);

  /**
   * Triggers file analysis and advances to the analysis step.
   */
  const triggerAnalysis = useCallback(
    async (id: string) => {
      try {
        const result = await analyzeFile.mutateAsync(id);
        if (
          result.status === 'analyzed' &&
          result.headers &&
          result.sampleData
        ) {
          setAnalysisData({
            headers: result.headers,
            sampleData: result.sampleData,
            rowCount: result.rowCount || 0,
          });
        } else if (result.status === 'needs_sheet_selection') {
          setAnalysisData({
            headers: [],
            sampleData: [],
            rowCount: 0,
            sheets: result.sheets || [],
          });
        }
        setStep('analysis');
      } catch {
        // Error is handled by the mutation's onError callback
      }
    },
    [analyzeFile, setAnalysisData, setStep]
  );

  /**
   * Called when a new file upload completes successfully.
   * The useFileUpload hook already sets fileData in the store.
   */
  const handleUploadComplete = useCallback(async () => {
    // Invalidate file list cache so new uploads appear
    queryClient.invalidateQueries({ queryKey: ['lead-import', 'files'] });
    // fileID is set by useFileUpload hook in the store
    const currentFileID = useImportWizardStore.getState().fileID;
    if (currentFileID) {
      await triggerAnalysis(currentFileID);
    }
  }, [triggerAnalysis, queryClient]);

  /**
   * Called when a user selects a previously uploaded file.
   */
  const handleSelectPreviousFile = useCallback(
    async (file: FileInfo) => {
      if (!file.fileID) {
        console.error('Cannot analyze file: missing fileID', file);
        return;
      }
      setFileData({ fileID: file.fileID, filename: file.filename });
      await triggerAnalysis(file.fileID);
    },
    [setFileData, triggerAnalysis]
  );

  return (
    <Box data-testid="file-upload-step">
      <Typography variant="h6" gutterBottom>
        Upload File
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a CSV or Excel file containing your lead data, or select a
        previously uploaded file.
      </Typography>

      <FileDropZone onUploadComplete={handleUploadComplete} />

      <PreviousFilesList onSelectFile={handleSelectPreviousFile} />
    </Box>
  );
};

export default FileUploadStep;
